import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUpdateSettings } from "@/features/settings/hooks/useSettingsMutations";
import {
  allowedOriginsValidation,
  type AllowedOriginsFormValues,
} from "@/validations/settings";

interface AllowedOriginsFormProps {
  allowedOrigins?: string[];
}

export function AllowedOriginsForm({
  allowedOrigins,
}: AllowedOriginsFormProps) {
  const updateMutation = useUpdateSettings();

  const formMethods = useForm<AllowedOriginsFormValues>({
    values: {
      origins: allowedOrigins?.length
        ? allowedOrigins.map((value) => ({ value }))
        : [{ value: "" }],
    },
    resolver: zodResolver(allowedOriginsValidation),
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: "origins",
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = formMethods;

  const isPending = isSubmitting || updateMutation.isPending;

  async function onSubmit(data: AllowedOriginsFormValues) {
    await updateMutation.mutateAsync({
      allowedOrigins: data.origins
        .map((o) => o.value.trim())
        .filter((v) => v.length > 0),
    });
  }

  return (
    <Form {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          روابط الموقع الإلكتروني ولوحة التحكم المسموح لها بالوصول (CORS). يتم
          دائماً السماح بالروابط المهيّأة عبر متغيرات البيئة.
        </p>

        <div className="space-y-2">
          {fields.map((fieldItem, index) => (
            <FormField
              key={fieldItem.id}
              control={control}
              name={`origins.${index}.value`}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input placeholder="https://example.com" {...field} />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => append({ value: "" })}
        >
          <Plus className="ml-2 h-4 w-4" />
          إضافة رابط
        </Button>

        <div>
          <Button type="submit" disabled={isPending}>
            حفظ الروابط المسموح بها
            {isPending && <Loader className="mr-2" />}
          </Button>
        </div>
      </form>
    </Form>
  );
}
